import type { CaseStage, Cents, Confidence, RecommendationKind } from "./types";

/** Dollars → cents. Use at the edge; never build a `Cents` by hand. */
export function dollars(amount: number): Cents {
  return Math.round(amount * 100);
}

/** $450,000 — exact, no abbreviation. Use where the figure is committed to. */
export function formatMoney(cents: Cents): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/** $450K / $1.2M — for scanning. Use in tables and headline figures. */
export function formatCompactMoney(cents: Cents): string {
  const value = cents / 100;
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${trimZero(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}$${trimZero(abs / 1_000)}K`;
  return `${sign}$${Math.round(abs)}`;
}

function trimZero(value: number): string {
  const rounded = value >= 100 ? value.toFixed(0) : value.toFixed(value >= 10 ? 1 : 2);
  return rounded.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
}

/** Signed compact money, for adjustment lines: `+ $60K`, `- $35K`. */
export function formatSignedMoney(cents: Cents): string {
  const sign = cents < 0 ? "−" : "+";
  return `${sign} ${formatCompactMoney(Math.abs(cents))}`;
}

export function formatPercent(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}

const STAGE_LABELS: Record<CaseStage, string> = {
  data_collection: "Data Collection",
  underwriting: "Underwriting",
  approval: "Approval",
  approved: "Approved",
  rejected: "Rejected",
  closed: "Closed",
};

export function formatStage(stage: CaseStage): string {
  return STAGE_LABELS[stage];
}

const RECOMMENDATION_LABELS: Record<RecommendationKind, string> = {
  approve: "Approve",
  approve_with_conditions: "Approve with conditions",
  decline: "Decline",
  more_info: "More information required",
};

export function formatRecommendation(kind: RecommendationKind): string {
  return RECOMMENDATION_LABELS[kind];
}

export function formatConfidence(confidence: Confidence): string {
  return confidence.charAt(0).toUpperCase() + confidence.slice(1);
}

/*
 * Timestamps render in UTC rather than the viewer's local zone.
 *
 * An audit trail is evidence: two people reviewing the same case from different
 * offices must quote the same time for the same event, and a screenshot of a
 * decision has to mean one thing. It also keeps the fixtures deterministic.
 * A real deployment would make this the institution's booking timezone.
 */
const ZONE = "UTC";

/** `14:22` — activity timelines are read within a working day. */
export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: ZONE,
  });
}

/** `23 Aug 2026`. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: ZONE,
  });
}

export function formatDateTime(iso: string): string {
  return `${formatDate(iso)}, ${formatTime(iso)}`;
}

/** Groups a timeline by calendar day, newest first, for date headers. */
export function formatDayHeading(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: ZONE,
  });
}

export function formatWaiting(days: number): string {
  if (days === 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
}
